import * as React from "react";
import { AutoComplete, Card, Button } from "antd";
import { MinusCircleOutlined } from "@ant-design/icons";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { CardContent } from "../../components/antd/card/CardContent";
import { CpArrayObject } from "./testNewForm/CpArrayObject";
import { CpInputNumber } from "./testNewForm/CpInputNumber";
import { CpErrorWarning } from "./testNewForm/CpErrorWarning";
import { CpTooltip } from "./testNewForm/CpTooltip";
import { FormListFieldItem } from "./formListFieldItem";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { formSet, formSetValue, formTriggerValidation, useCpFormValue } from "./testNewForm/utils";
import { store } from "../../store";
import { cloneDeep, get } from "lodash";
import { KindSelectState } from "../../components/antd/kindselect/KindSelect";
import { LocationOptionItemInput } from "../../schema/types/gvc/input";
import { parseLink } from "../../utils/linkParser/linkParser";
import { v4 as uuidv4 } from "uuid";

const COLUMN_COUNT = 9;

const labelHeaderClass = "text-xs cpln-ant-form-item-description font-semibold flex items-center gap-1";

function LabelHeader({ label, tooltip, span }: { label: string; tooltip: string; span: number }) {
  return (
    <div className={`col-span-${span} ${labelHeaderClass}`}>
      {label.toUpperCase()}
      <CpTooltip tooltipProps={{ title: tooltip }} />
    </div>
  );
}

function LocationOptionsLabels() {
  return (
    <div
      className="gap-2 mb-1"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${COLUMN_COUNT}, 1fr) 32px`,
      }}
    >
      <LabelHeader
        label="Location"
        tooltip="The location to configure routing options for. Select from locations already added to this GVC, or type a location name manually."
        span={3}
      />
      <LabelHeader
        label="Routing Tier"
        tooltip="DNS-level failover tier. Locations with a lower tier value are preferred. All locations in the same tier form a group that is resolved by latency. If every location in the highest-priority tier becomes unavailable, traffic falls back to the next tier."
        span={2}
      />
      <LabelHeader
        label="Latency Offset (ms)"
        tooltip="An artificial offset added to the measured latency during DNS geo routing. Use a negative value to make a location more attractive to traffic, or a positive value to push traffic away. For example, -20 effectively makes the location appear 20 ms closer than it actually is."
        span={2}
      />
      <LabelHeader
        label="Latency Tolerance (ms)"
        tooltip="The maximum acceptable latency in milliseconds. If the measured latency to this location exceeds this threshold, the location is treated as unavailable for DNS geo routing and traffic is routed to the next best option."
        span={2}
      />
      <div />
    </div>
  );
}

interface Props {}
export const LocationOptions: React.FC<Props> = () => {
  const { id } = useCpFormContext();
  const locationOptions: LocationOptionItemInput[] = useCpFormValue("locationOptions");
  const hasItems = locationOptions && locationOptions.length > 0;

  const factoryFn = React.useCallback(() => {
    const form = store.getState().form.instances[id];
    const currentLocationItems: KindSelectState[] = form.values.locationItems || [];
    const currentOptions: LocationOptionItemInput[] = form.values.locationOptions || [];

    // First unused location from the GVC's selected locations
    const usedLinks = new Set(currentOptions.map((o) => o.locationLink));
    const firstUnused = currentLocationItems.find((l) => !usedLinks.has(l.link));

    // Routing tier: 0 for the first item, highest existing tier for subsequent items
    const tiers = currentOptions.map((o) => Number(o.routingTier)).filter((n) => !isNaN(n));
    const routingTier = currentOptions.length === 0 ? "0" : String(Math.max(...tiers, 0));

    // Increment change counter so the dirty checker detects the pre-filled item.
    // Other CpArrayObject usages don't need this because their factories create empty items
    // that force the user to type into Cp* inputs (which increment the counter themselves).
    formSet(id, `meta.onChangeCounter`, form.meta.onChangeCounter + 1);

    return {
      id: uuidv4(),
      locationLink: firstUnused?.link || "",
      routingTier,
      latencyOffsetMs: "",
      latencyToleranceMs: "",
    };
  }, [id]);

  return (
    <div className="cpln-form-section cpln-ant-card-container">
      <Card>
        <CardTitle>Location Options</CardTitle>
        <CardContent>
          <p className="mb-4 text-sm" style={{ color: "var(--color-label)" }}>
            Configure per-location DNS geo routing options. Locations with a lower routing tier have higher priority. Within the same tier, the lowest-latency location wins. If all locations in the highest-priority tier become unavailable, traffic falls back to the next tier.
          </p>
          {hasItems ? <LocationOptionsLabels /> : null}
          <CpArrayObject
            name="locationOptions"
            factoryFn={factoryFn}
          >
            <CpArrayItemLocationOption />
          </CpArrayObject>
        </CardContent>
      </Card>
    </div>
  );
};

interface ItemProps {
  name?: string;
  item?: LocationOptionItemInput;
  index?: number;
}
const CpArrayItemLocationOption: React.FC<ItemProps> = React.memo(({ name: _name, item: _item, index: _index }) => {
  const { id } = useCpFormContext();

  const name = _name!;
  const item = _item!;
  const index = _index!;

  const locationItems: KindSelectState[] = useCpFormValue("locationItems");

  const autocompleteOptions = React.useMemo(() => {
    return locationItems.map((l) => ({
      label: l.value,
      value: l.value,
    }));
  }, [locationItems]);

  // Display the location name instead of the full link
  const [displayValue, setDisplayValue] = React.useState(() => {
    if (!item.locationLink) return "";
    const { name: locationName } = parseLink(item.locationLink, { useInputCtx: true });
    return locationName || item.locationLink;
  });

  // Sync display value when form state changes externally (e.g. on load)
  React.useEffect(() => {
    if (!item.locationLink) {
      setDisplayValue("");
      return;
    }
    const { name: locationName } = parseLink(item.locationLink, { useInputCtx: true });
    setDisplayValue(locationName || item.locationLink);
  }, [item.locationLink]);

  function remove() {
    const form = store.getState().form.instances[id];
    const values = form.values;
    const items = get(values, name) || [];
    let _items = cloneDeep(items);
    _items = _items.filter((i: LocationOptionItemInput) => i.id !== item.id);
    formSetValue(id, name, _items);
    formTriggerValidation(id);
  }

  function incrementChangeCounter() {
    const form = store.getState().form.instances[id];
    formSet(id, `meta.onChangeCounter`, form.meta.onChangeCounter + 1);
  }

  function onSelectLocation(value: string) {
    const match = locationItems.find((l) => l.value === value);
    if (match) {
      setDisplayValue(match.value);
      formSetValue(id, `${name}.${index}.locationLink`, match.link);
    } else {
      setDisplayValue(value);
      formSetValue(id, `${name}.${index}.locationLink`, value);
    }
    incrementChangeCounter();
    formTriggerValidation(id);
  }

  function onChangeLocation(value: string) {
    setDisplayValue(value);
    const match = locationItems.find((l) => l.value === value);
    if (match) {
      formSetValue(id, `${name}.${index}.locationLink`, match.link);
    } else {
      formSetValue(id, `${name}.${index}.locationLink`, value);
    }
    incrementChangeCounter();
  }

  return (
    <div className="mb-1">
      <FormListFieldItem columnCount={COLUMN_COUNT} hasRemoveButton>
        <div className="col-span-3" style={{ minWidth: 0 }}>
          <AutoComplete
            options={autocompleteOptions}
            placeholder="Select location"
            style={{ width: "100%" }}
            value={displayValue}
            onSelect={onSelectLocation}
            onChange={onChangeLocation}
            filterOption={(inputValue, option) =>
              (option?.label as string || "").toLowerCase().includes(inputValue.toLowerCase())
            }
          />
        </div>
        <div className="col-span-2">
          <CpInputNumber name={`${name}.${index}.routingTier`} min={0} precision={0} placeholder="-" className="w-full free-width" />
        </div>
        <div className="col-span-2">
          <CpInputNumber name={`${name}.${index}.latencyOffsetMs`} precision={0} placeholder="-" className="w-full free-width" />
        </div>
        <div className="col-span-2">
          <CpInputNumber name={`${name}.${index}.latencyToleranceMs`} min={0} precision={0} placeholder="-" className="w-full free-width" />
        </div>
        <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove()} />
      </FormListFieldItem>
      <CpErrorWarning name={`${name}.${index}.locationLink`} arrayItem />
    </div>
  );
});
